export type FetchJsonResult<T> =
  | { ok: true; data: T }
  | { ok: false; reason: 'http-error' | 'exception'; error: string };

/**
 * Fetches and parses JSON in a single attempt, normalizing both HTTP
 * failures and thrown exceptions into a single result shape.
 *
 * @param url URL to fetch
 * @returns Standardized FetchJsonResult with data or error reason
 */
export async function fetchJsonOnce<T>(url: string): Promise<FetchJsonResult<T>> {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      return {
        ok: false,
        reason: 'http-error',
        error: `Request to ${url} failed with status ${response.status}`,
      };
    }
    const data = (await response.json()) as T;
    return { ok: true, data };
  } catch (err) {
    return {
      ok: false,
      reason: 'exception',
      error: err instanceof Error ? err.message : `Failed to fetch ${url}`,
    };
  }
}

const RETRY_DELAYS_MS = [500, 1500];

/**
 * Retries `fetchJsonOnce` with backoff, stopping at the first success.
 * Used when a user explicitly retries a failed feed.
 *
 * @param url URL to fetch
 * @returns Standardized FetchJsonResult
 */
export async function fetchJsonWithBackoff<T>(url: string): Promise<FetchJsonResult<T>> {
  let result = await fetchJsonOnce<T>(url);
  for (let attempt = 0; attempt < RETRY_DELAYS_MS.length && !result.ok; attempt++) {
    await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS_MS[attempt]));
    result = await fetchJsonOnce<T>(url);
  }
  if (!result.ok) {
    console.error(`Failed to fetch ${url} after retrying:`, result.error);
  }
  return result;
}
